/* eslint-disable react/destructuring-assignment */
import { selectHasFieldError } from '@presentation/utils';
import { useField } from 'formik';
import { sortBy, uniq } from 'lodash/fp';
import type { FormEventHandler, PropsWithChildren } from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { FaAngleDown } from 'react-icons/fa';
import type { SelectOption } from '..';
import { DropdownSelectSearch, ErrorText, InputFieldClearButton } from '..';
import { Input } from '../Input';
import type { InputSelectFieldProps } from './types';

export * from './types';

export const InputSelectField = <TValue,>(
  props: PropsWithChildren<InputSelectFieldProps<TValue>>,
) => {
  const {
    name,
    onSelect,
    options,
    placeholder,
    dismissOnClick = true,
    useSearch = props.options.length > 10,
    sortBySelected = props.options.length > 10,
    clearButton,
    disabled = false,
    multiple = false,
    hasDropdownIcon = false,
    iconRight,
    noEmpty = false,
    dropdownProps,
    alwaysTreatAsArray,
    disableErrors = false,
    ...rest
  } = props;
  const [field, meta, helpers] = useField<TValue | TValue[] | undefined>(name);

  const treatAsMultiple = alwaysTreatAsArray || multiple;

  const inputRef = useRef<HTMLInputElement>(null);
  const [search, setSearch] = useState<string>(); // CARE! Using state in presentation exceptionally

  const valueAsArray: TValue[] = useMemo(() => {
    return (field.value as TValue[]) ?? [];
  }, [field.value]);

  const onSearchInput: FormEventHandler<HTMLInputElement> = useCallback((event) => {
    setSearch((event.target as HTMLInputElement).value);
  }, []);

  const formattedOptions = useMemo((): SelectOption<TValue>[] => {
    if (treatAsMultiple && !!valueAsArray.length && sortBySelected) {
      return sortBy((x) => !valueAsArray.includes(x.value), options);
    }
    if (!treatAsMultiple && !!field.value && sortBySelected) {
      return sortBy((x) => field.value !== x.value, options);
    }
    return options;
  }, [field.value, options, sortBySelected, treatAsMultiple, valueAsArray]);

  const valueLabel = useMemo((): string => {
    // if (search != null) return search;

    if (treatAsMultiple) {
      const optionsFromValue = formattedOptions.filter((x) =>
        (valueAsArray ?? []).includes(x.value),
      );

      if (!optionsFromValue.length) return '';

      return `(${optionsFromValue.length}) ${optionsFromValue.map((x) => x.label).join(', ')}`;
    }
    return formattedOptions.find((option) => option.value === field.value)?.label?.toString() ?? '';
  }, [treatAsMultiple, formattedOptions, valueAsArray, field.value]);

  const dropdownSelectHandler = useCallback(
    (selected: SelectOption<TValue>) => {
      helpers.setTouched(true);
      let newValue: TValue | TValue[] | undefined = selected.value;

      const element = inputRef?.current?.parentElement
        ?.getElementsByClassName?.('simplebar-content-wrapper')
        ?.item(0);
      if (element) {
        if (dismissOnClick) {
          element.scrollTop = 0;
        } else {
          element.scrollTo?.({ left: 0, top: 0, behavior: 'smooth' });
        }
      }

      if (multiple) {
        if (valueAsArray.includes(selected.value)) {
          newValue = valueAsArray.filter((v) => v !== selected.value);
        } else {
          newValue = uniq([...valueAsArray, selected.value]);
        }
      } else if (treatAsMultiple) {
        newValue = [selected.value];
        if (valueAsArray.includes(selected.value)) {
          newValue = valueAsArray.filter((v) => v !== selected.value);
        }
      } else if (newValue === field.value) {
        newValue = undefined;
      }

      helpers.setValue(newValue);
      onSelect?.(selected, newValue);
      setSearch(undefined);

      if (dismissOnClick && document.activeElement) {
        (document.activeElement as HTMLElement).blur?.();
      }
    },
    // no helpers here, it is causing rerenders https://github.com/jaredpalmer/formik/issues/2268
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dismissOnClick, field.value, multiple, onSelect, treatAsMultiple, valueAsArray],
  );

  const dropdownSelectSearch = useMemo(() => {
    return (
      <DropdownSelectSearch
        multiple={treatAsMultiple}
        value={field.value}
        inputProps={{
          style: { display: useSearch ? 'inherit' : 'none' },
          onInput: onSearchInput,
        }}
        search={search}
        onSelect={dropdownSelectHandler}
        options={formattedOptions}
        noEmpty={noEmpty}
        {...dropdownProps}
      />
    );
  }, [
    field.value,
    formattedOptions,
    treatAsMultiple,
    useSearch,
    onSearchInput,
    search,
    dropdownSelectHandler,
    noEmpty,
    dropdownProps,
  ]);

  const hasError = useMemo(() => {
    if (disableErrors) {
      return false;
    }
    return selectHasFieldError(meta);
  }, [disableErrors, meta]);

  return (
    <>
      <Input
        {...rest}
        ref={inputRef}
        disabled={disabled}
        name={name}
        autoComplete="off"
        onBlur={field.onBlur}
        value={valueLabel}
        onInput={onSearchInput}
        placeholder={placeholder}
        hasError={hasError}
        textDisabled
        iconRight={
          <InputFieldClearButton<TValue>
            {...clearButton}
            componentOnHidden={!!hasDropdownIcon && <FaAngleDown data-dropdown-icon />}
            value={field.value as any}
            initialValue={meta.initialValue}
            multiple={treatAsMultiple}
            onClear={(newValue) => {
              if (disabled) return;
              helpers.setValue(newValue);
              clearButton?.onClear?.(newValue);
            }}
          >
            {iconRight}
          </InputFieldClearButton>
        }
        dropdown={disabled ? undefined : dropdownSelectSearch}
      />
      {hasError && <ErrorText>{meta.error}</ErrorText>}
    </>
  );
};
